import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  RefreshCw,
  ExternalLink,
  AlertCircle,
  CheckCircle,
  AlertTriangle,
  Info,
  FileText,
  Activity,
} from 'lucide-react';
import { api } from '../../services/api';

interface HealthIssue {
  severity: 'critical' | 'warning' | 'info';
  category: string;
  message: string;
  affected_count?: number;
  store_ids?: string[];
}

interface HealthMetrics {
  seed_id: number;
  seed_name: string;
  report_date: string;
  total_products: number;
  products_updated_24h: number;
  products_updated_48h: number;
  products_stale: number;
  products_missing_price: number;
  products_missing_image: number;
  products_missing_name: number;
  stores_checked: number;
  stores_with_data: number;
  stores_with_errors: string[];
  error_summary: Record<string, number>;
  health_score: number;
  issues: HealthIssue[];
}

function HealthScoreCircle({ score }: { score: number }) {
  const color = score >= 80 ? 'text-green-500' :
                score >= 60 ? 'text-yellow-500' :
                score >= 40 ? 'text-orange-500' : 'text-red-500';

  const bgColor = score >= 80 ? 'bg-green-100' :
                  score >= 60 ? 'bg-yellow-100' :
                  score >= 40 ? 'bg-orange-100' : 'bg-red-100';

  const label = score >= 80 ? 'Healthy' :
                score >= 60 ? 'Needs Attention' :
                score >= 40 ? 'Degraded' : 'Critical';

  return (
    <div className="flex items-center gap-4">
      <div className={`w-20 h-20 ${bgColor} rounded-full flex items-center justify-center`}>
        <span className={`text-3xl font-bold ${color}`}>{score}</span>
      </div>
      <div>
        <div className="text-sm text-gray-500">Health Score</div>
        <div className={`text-lg font-semibold ${color}`}>{label}</div>
      </div>
    </div>
  );
}

function IssueCard({ issue }: { issue: HealthIssue }) {
  const config = {
    critical: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      icon: AlertCircle,
      iconColor: 'text-red-500',
    },
    warning: {
      bg: 'bg-yellow-50',
      border: 'border-yellow-200',
      icon: AlertTriangle,
      iconColor: 'text-yellow-500',
    },
    info: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      icon: Info,
      iconColor: 'text-blue-500',
    },
  };

  const { bg, border, icon: Icon, iconColor } = config[issue.severity];

  return (
    <div className={`p-4 rounded-lg ${bg} border ${border}`}>
      <div className="flex items-start gap-3">
        <Icon className={`${iconColor} flex-shrink-0 mt-0.5`} size={20} />
        <div className="flex-1">
          <p className="text-gray-900">{issue.message}</p>
          {issue.affected_count && (
            <p className="text-sm text-gray-500 mt-1">
              {issue.affected_count.toLocaleString()} items affected
            </p>
          )}
          {issue.store_ids && issue.store_ids.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {issue.store_ids.map((storeId) => (
                <span
                  key={storeId}
                  className="px-2 py-0.5 bg-white rounded text-xs text-gray-600"
                >
                  {storeId}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  subValue,
  color = 'gray',
}: {
  label: string;
  value: string | number;
  subValue?: string;
  color?: 'gray' | 'green' | 'yellow' | 'red';
}) {
  const colors = {
    gray: 'text-gray-900',
    green: 'text-green-600',
    yellow: 'text-yellow-600',
    red: 'text-red-600',
  };

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className={`text-2xl font-bold ${colors[color]}`}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      <div className="text-sm text-gray-500">{label}</div>
      {subValue && <div className="text-xs text-gray-400 mt-1">{subValue}</div>}
    </div>
  );
}

export default function SeedHealthReport() {
  const { seedId } = useParams<{ seedId: string }>();
  const [metrics, setMetrics] = useState<HealthMetrics | null>(null);

  const queryClient = useQueryClient();

  // Fetch seed details
  const { data: seedData } = useQuery({
    queryKey: ['admin-seed', seedId],
    queryFn: () => api.getAdminSeed(parseInt(seedId!)),
    enabled: !!seedId,
  });

  // Run health check mutation
  const healthCheckMutation = useMutation({
    mutationFn: () => api.runAdminSeedHealthCheck(parseInt(seedId!)),
    onSuccess: (data) => {
      setMetrics(data);
      queryClient.invalidateQueries({ queryKey: ['admin-seed-health', seedId] });
    },
  });

  // Auto-run health check on mount if no metrics
  const { isLoading: initialLoading } = useQuery({
    queryKey: ['admin-seed-health-initial', seedId],
    queryFn: async () => {
      const result = await api.runAdminSeedHealthCheck(parseInt(seedId!));
      setMetrics(result);
      return result;
    },
    enabled: !!seedId && !metrics,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const isLoading = initialLoading || healthCheckMutation.isPending;

  if (!seedId) {
    return (
      <div className="text-center py-12 text-gray-500">
        No seed ID provided
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/admin/seeds"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </Link>
          <div>
            <h1 className="text-xl font-semibold">
              Health Report: {seedData?.name || `Seed #${seedId}`}
            </h1>
            <p className="text-sm text-gray-500">
              {seedData?.country} | {seedData?.timezone}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <a
            href={`/api/admin/seeds/${seedId}/health/report`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <FileText size={16} />
            View Full Report
            <ExternalLink size={14} />
          </a>
          <button
            onClick={() => healthCheckMutation.mutate()}
            disabled={isLoading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
            {isLoading ? 'Running...' : 'Run Health Check'}
          </button>
        </div>
      </div>

      {isLoading && !metrics ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : metrics ? (
        <>
          {/* Health Score */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <HealthScoreCircle score={metrics.health_score} />
              <div className="text-right text-sm text-gray-500">
                <div>Report Date: {metrics.report_date}</div>
                <div>
                  {metrics.stores_with_data}/{metrics.stores_checked} stores with data
                </div>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Activity size={20} />
              Statistics
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                label="Total Products"
                value={metrics.total_products}
              />
              <StatCard
                label="Updated (24h)"
                value={metrics.products_updated_24h}
                subValue={`${metrics.total_products > 0 ? Math.round((metrics.products_updated_24h / metrics.total_products) * 100) : 0}%`}
                color={
                  metrics.total_products > 0 &&
                  metrics.products_updated_24h / metrics.total_products >= 0.7
                    ? 'green'
                    : 'yellow'
                }
              />
              <StatCard
                label="Updated (48h)"
                value={metrics.products_updated_48h}
                subValue={`${metrics.total_products > 0 ? Math.round((metrics.products_updated_48h / metrics.total_products) * 100) : 0}%`}
                color="green"
              />
              <StatCard
                label="Stale (7+ days)"
                value={metrics.products_stale}
                color={metrics.products_stale > 0 ? 'red' : 'green'}
              />
              <StatCard
                label="Missing Price"
                value={metrics.products_missing_price}
                color={metrics.products_missing_price > 0 ? 'red' : 'green'}
              />
              <StatCard
                label="Missing Image"
                value={metrics.products_missing_image}
                color={metrics.products_missing_image > 0 ? 'yellow' : 'green'}
              />
              <StatCard
                label="Stores Checked"
                value={metrics.stores_checked}
              />
              <StatCard
                label="Stores with Errors"
                value={metrics.stores_with_errors.length}
                color={metrics.stores_with_errors.length > 0 ? 'red' : 'green'}
              />
            </div>
          </div>

          {/* Issues */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <AlertCircle size={20} />
              Issues ({metrics.issues.length})
            </h2>

            {metrics.issues.length === 0 ? (
              <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg text-green-700">
                <CheckCircle size={20} />
                <span>No issues detected. This seed is operating normally.</span>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Critical issues first */}
                {metrics.issues
                  .filter((i) => i.severity === 'critical')
                  .map((issue, idx) => (
                    <IssueCard key={`critical-${idx}`} issue={issue} />
                  ))}
                {/* Warning issues */}
                {metrics.issues
                  .filter((i) => i.severity === 'warning')
                  .map((issue, idx) => (
                    <IssueCard key={`warning-${idx}`} issue={issue} />
                  ))}
                {/* Info issues */}
                {metrics.issues
                  .filter((i) => i.severity === 'info')
                  .map((issue, idx) => (
                    <IssueCard key={`info-${idx}`} issue={issue} />
                  ))}
              </div>
            )}
          </div>

          {/* Error Summary */}
          {Object.keys(metrics.error_summary).length > 0 && (
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h2 className="text-lg font-semibold mb-4">
                Error Summary (Last 7 Days)
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-4 font-medium">
                        Error Type
                      </th>
                      <th className="text-right py-2 px-4 font-medium">
                        Count
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(metrics.error_summary).map(
                      ([error, count]) => (
                        <tr key={error} className="border-b border-gray-100">
                          <td className="py-2 px-4 text-gray-600">{error}</td>
                          <td className="py-2 px-4 text-right text-red-600 font-medium">
                            {count}
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Stores with Errors */}
          {metrics.stores_with_errors.length > 0 && (
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h2 className="text-lg font-semibold mb-4">
                Stores with Errors ({metrics.stores_with_errors.length})
              </h2>
              <div className="flex flex-wrap gap-2">
                {metrics.stores_with_errors.map((storeId) => (
                  <span
                    key={storeId}
                    className="px-3 py-1.5 bg-red-50 text-red-700 rounded-lg text-sm"
                  >
                    {storeId}
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <AlertCircle className="mx-auto mb-4 text-gray-400" size={48} />
          <p className="text-gray-500">
            Click "Run Health Check" to generate a health report for this seed.
          </p>
        </div>
      )}
    </div>
  );
}
